import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClient } from "../api";
import { isResponseSuccess } from "../utils";
import { URLS } from "../constants";
import { UserType } from "../all-types";

/**
 * Get all users
 */
export const getUserList = createAsyncThunk(
  "users/getUserList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(URLS.GET_USERS);
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to fetch users");
      }
      return response.data.data as UserType[];
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not fetch users");
    }
  }
);

/**
 * Create a new user
 */
export const createUser = createAsyncThunk(
  "users/createUser",
  async (details: { user: Omit<UserType, '_id'> }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.post(URLS.ADD_USER, { user: details.user });
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to add user");
      }
      dispatch(getUserList());
      return response.data.data as UserType;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not add user");
    }
  }
);

/**
 * Update an existing user
 */
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async (details: { id: string; user: Omit<UserType, '_id'> }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.put(URLS.UPDATE_USER(details.id), {user: details.user});
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to update user");
      }
      dispatch(getUserList());
      return response.data.data as UserType;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not update user");
    }
  }
);

/**
 * Delete a user
 */
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosClient.delete(URLS.DELETE_USER(id));
      if (!isResponseSuccess(response.status)) {
        throw new Error("Failed to delete user");
      }
      dispatch(getUserList());
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Could not delete user");
    }
  }
);
